export function calculateHandicap(heightInches: number): number {
  if (heightInches >= 18) return 1.0;
  if (heightInches >= 12) return 1.5;
  return 2.0;
}

export function calculateSpeed(timeSeconds: number): number {
  return (100 / timeSeconds) * (3600 / 1760);
}

export function calculatePoints(speedMph: number, handicap: number): number {
  return speedMph * handicap;
}

// Year-scoped stats derived from a runs array. Used for the "[Year] Performance
// Stats" card at the top of a dog profile. Returns the same shape as the slice
// of DogStats the StatsCard consumes — but only for runs in `year`.
//
// `totalPoints` here is the SUM of points earned in that year (not cumulative).
// The top card supplies a separate `totalPointsAllTime` prop for the lifetime
// override the design calls for.
export interface YearScopedStats {
  totalRuns: number;
  personalBestTime: number;
  personalBestSpeed: number;
  totalPoints: number;
  bestThreeAvgSpeed: number;
  run1AvgSpeed: number;
  run2AvgSpeed: number;
  run1Count: number;
  run2Count: number;
}

interface RunLike {
  date: string;
  runNumber: number;
  timeSeconds: string | number;
  speedMph: string | number;
  points: string | number;
}

export function computeYearStats(runs: RunLike[], year: number): YearScopedStats {
  const yearRuns = runs.filter((r) => new Date(r.date).getFullYear() === year);
  if (yearRuns.length === 0) {
    return {
      totalRuns: 0,
      personalBestTime: 0,
      personalBestSpeed: 0,
      totalPoints: 0,
      bestThreeAvgSpeed: 0,
      run1AvgSpeed: 0,
      run2AvgSpeed: 0,
      run1Count: 0,
      run2Count: 0,
    };
  }
  const speeds = yearRuns.map((r) => Number(r.speedMph));
  const times = yearRuns.map((r) => Number(r.timeSeconds));
  const points = yearRuns.map((r) => Number(r.points) || 0);
  const top3 = [...speeds].sort((a, b) => b - a).slice(0, 3);
  const bestThreeAvgSpeed = top3.length ? top3.reduce((a, b) => a + b, 0) / top3.length : 0;
  const run1 = yearRuns.filter((r) => r.runNumber === 1).map((r) => Number(r.speedMph));
  const run2 = yearRuns.filter((r) => r.runNumber === 2).map((r) => Number(r.speedMph));
  return {
    totalRuns: yearRuns.length,
    personalBestTime: Math.min(...times),
    personalBestSpeed: Math.max(...speeds),
    totalPoints: points.reduce((a, b) => a + b, 0),
    bestThreeAvgSpeed,
    run1AvgSpeed: run1.length ? run1.reduce((a, b) => a + b, 0) / run1.length : 0,
    run2AvgSpeed: run2.length ? run2.reduce((a, b) => a + b, 0) / run2.length : 0,
    run1Count: run1.length,
    run2Count: run2.length,
  };
}
